import { Button, Divider, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import Link from 'next/link';
import React, { FC, useCallback, useState } from 'react';
import { Form, FormSchema, SchemaFormItems } from '../../components/form/Form';
import { useConnection } from '../../connection/Application';
import ReCAPTCHA from 'react-google-recaptcha';
import styles from './signup.module.less';
import { Text } from '../../components/Typogrphy/Text';
import { FlexBox } from '../../components/FlexBox';
import Config from '../../config';
import AuthLayout from './AuthLayout';
import { SocialLoginButtons } from './SocialLoginButtons';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { NextSeo } from 'next-seo';
import { DEFAULT_OG_IMAGES, SEO_COMMON } from '../../layout/candidate/CandidateLayout';

const { Title } = Typography;
const ReCapt: any = ReCAPTCHA;

const schema: FormSchema = [
  // {
  //   id: 'image',
  //   type: 'file',
  //   name: 'File',
  //   props: {
  //     placeholder: 'First Name',
  //     image: { width: 128, height: 128, crop: true },
  //     type: 'drag',
  //   },
  //   options: {
  //     rules: [{ required: true, message: 'File is required' }],
  //   },
  // },
  {
    id: 'firstName',
    type: 'string',
    name: 'First Name',
    props: { placeholder: 'First Name' },
    options: {
      rules: [{ required: true, message: 'First name is required' }],
    },
  },
  {
    id: 'lastName',
    type: 'string',
    name: 'Last Name',
    props: { placeholder: 'Last Name' },
    options: {
      rules: [{ required: true, message: 'Last Name is required' }],
    },
  },
  {
    id: 'email',
    type: 'string',
    name: 'Email Address',
    props: { placeholder: 'example@email.com' },
    options: {
      rules: [
        { required: true, message: 'Email Address is required' },
        { type: 'email', message: 'Invalid Email' },
      ],
    },
  },
  {
    id: 'password',
    type: 'password',
    name: 'Password',
    props: { placeholder: 'Enter your password' },
    options: {
      rules: [
        { required: true, message: 'Password is required' },
        {
          validator: 'passwordPolicy',
          message:
            'Password should contain more than 8 characters and at least 1 upper case character, ' +
            '1 lower case character, 1 number and 1 special character',
        },
      ],
    },
  },
  {
    id: 'confirmPassword',
    type: 'password',
    name: 'Confirm Password',
    props: { placeholder: 'Confirm your Password' },
    options: {
      dependencies: ['password'],
      rules: [
        { required: true, message: 'Please confirm your password.' },
        {
          validator: 'equals',
          to: 'password',
          message: 'Your password and confirm password does not match',
        },
      ],
    },
  },
];

const SignupPage: FC = () => {
  const [cookie, setCookie, removeCookie] = useCookies(['referral']);
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = useForm();
  const connection = useConnection();
  const router = useRouter();
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const onSubmit = useCallback(
    async (formData) => {
      let reCaptchaToken = recaptchaRef.current?.getValue();
      if (reCaptchaToken === null || reCaptchaToken?.trim() === '') {
        reCaptchaToken = await recaptchaRef.current?.executeAsync();
      }
      if (reCaptchaToken) {
        setLoading(true);
        const user = { ...formData };
        delete user.confirmPassword;
        try {
          if (cookie.referral?.referralId) {
            await connection.post('auth', {
              user,
              reCaptchaToken,
              referralId: cookie.referral?.referralId,
            });
          } else {
            await connection.post('auth', { user, reCaptchaToken });
          }
          router.push(`/verification-sent/${formData.email}`);
        } catch (e: any) {
          if (e.body?.body?.hasFormErrors) {
            form.setFields(e.body?.body?.formErrors);
          }
        } finally {
          if (cookie.referral?.referralId) {
            removeCookie('referral', {
              path: '/',
              sameSite: true,
            });
          }
          setLoading(false);
        }
      }
    },
    [connection, cookie.referral?.referralId, form, removeCookie, router],
  );
  return (
    <React.Fragment>
      <NextSeo
        canonical="https://www.careers360.lk/sign-up"
        title="Sign Up | Careers360"
        description="Careers360 business focuses on giving our recruiters and candidates an innovative technology-driven top job portal in Sri Lanka."
        additionalMetaTags={[{ name: 'keywords', content: 'Recruiter| Careers360 | top job' }]}
        openGraph={{
          title: `Sign Up | Careers360`,
          description:
            'Careers360 business focuses on giving our recruiters and candidates an innovative technology-driven top job portal in Sri Lanka.',
          images: DEFAULT_OG_IMAGES,
        }}
        {...SEO_COMMON}
      />
      <AuthLayout>
        <Title className={styles.title} level={3}>
          Sign Up
        </Title>
        <div className={styles.signInLink}>
          <Text style={{ color: '#5B6272' }}>Already have an account?</Text>
          <span style={{ fontSize: 14, fontWeight: 'bold', letterSpacing: '0.14px' }}>
            <Link href={'/login'} className="gtm-track">
              <Typography className={styles.signup}>Sign In</Typography>
            </Link>
          </span>
        </div>
        {/*<Divider plain style={{ marginBottom: 30 }}>*/}
        {/*  <Text style={{ color: '#5B6272', fontSize: 14 }}>With Your</Text>*/}
        {/*</Divider>*/}
        <SocialLoginButtons />
        <Divider plain style={{ marginBottom: 30 }}>
          <Text style={{ color: '#5B6272', fontSize: 14 }}>or Sign Up</Text>
        </Divider>
        <Form
          onValuesChange={(changedValues, values) => console.log(values)}
          form={form}
          onFinish={onSubmit}
          layout="vertical"
          style={{ marginTop: 20 }}>
          <SchemaFormItems schema={schema} />
          {/*<FlexBox horizontalAlign={'center'}>*/}
          {/*  <ReCAPTCHA sitekey={Config.recaptchaSiteKey} size="normal" onChange={onChange} />*/}
          {/*</FlexBox>*/}
          <FlexBox horizontalAlign={'center'}>
            <ReCapt
              ref={recaptchaRef}
              sitekey={Config.recaptchaSiteKey}
              size="invisible"
              // onChange={(token) => onChange?.(token !== null ? token : undefined)}
              // onExpired={() => onChange?.(undefined)}
            />
          </FlexBox>
          <div style={{ marginTop: 20 }}>
            <Typography style={{ color: '#5B6272', fontSize: 14, textAlign: 'center' }}>
              {'By clicking "Sign Up", you agree to our'}{' '}
              <Link href={'/terms-of-service'}>terms of service</Link>
            </Typography>
            <Typography style={{ color: '#5B6272', fontSize: 14, textAlign: 'center' }}>
              and privacy statement.
            </Typography>
          </div>
          <Button
            htmlType="submit"
            type="primary"
            block
            loading={loading}
            style={{ marginTop: 36, marginBottom: 24 }}>
            Sign Up
          </Button>
        </Form>
      </AuthLayout>
    </React.Fragment>
  );
};

export default SignupPage;
